/**
 * Contrato universal de preferências de viagem (módulo puro — sem IA, sem
 * Maps, sem créditos).
 *
 * Representa, numa única estrutura, tudo o que o utilizador pediu e recusou:
 * categorias obrigatórias, categorias excluídas, itens nomeados (pratos,
 * lugares, experiências), restrições de rota e preferência de refeições.
 *
 * Regras invioláveis (iguais no Premium, no Pro e no planeamento
 * personalizado, em qualquer país):
 *  1. Exclusão explícita vence sempre um interesse automático ou herdado.
 *  2. Um pedido positivo explícito ("quero praias") é vinculativo.
 *  3. Um pedido novo NUNCA herda exclusões nem inclusões do pedido anterior:
 *     o contrato é reconstruído do texto atual + escolhas atuais do
 *     formulário.
 */

import {
  CATEGORY_TERMS,
  parseNegativePreferences,
  type ExcludedCategoryKind,
  type NegativePreferences,
} from "./negative-preferences";
import { extractLocalSpecialties, type LocalSpecialty } from "./local-specialties";
import { resolveMealPreference, type MealPreference } from "./meal-preference";
import { extractNamedItems } from "./catalog/taxonomy";

export type PreferenceCategory = ExcludedCategoryKind;

/** De onde veio cada decisão — usado no modo de diagnóstico da interface. */
export type PreferenceSource = "text" | "form" | "default";

export type RouteConstraints = {
  /** Corredor curado obrigatório (ex.: Estrada Nacional 2). */
  corridorId?: string;
  /** Não substituir o percurso oficial por autoestradas/atalhos. */
  followOfficialCorridor: boolean;
  /** Desvio máximo tolerado face à geometria oficial, em km. */
  maxDeviationKm?: number;
};

export type TripPreferences = {
  /** Categorias que TÊM de aparecer no roteiro. */
  requiredCategories: PreferenceCategory[];
  /** Categorias proibidas. Vencem sempre os interesses. */
  excludedCategories: PreferenceCategory[];
  /** Itens nomeados obrigatórios (pratos, produtos, experiências). */
  requiredNamedItems: string[];
  /** Itens nomeados proibidos. */
  excludedNamedItems: string[];
  /** Especialidades gastronómicas concretas pedidas. */
  foodSpecialties: LocalSpecialty[];
  routeConstraints: RouteConstraints;
  mealPreference: MealPreference;
  /** Origem de cada decisão (diagnóstico). */
  sources: Record<string, PreferenceSource>;
  /** Frases originais que geraram exclusões (depuração). */
  matchedPhrases: string[];
};

const fold = (value: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase();

/**
 * Lead-ins positivos multilingues. O grupo capturado é a janela onde se
 * procuram termos de categoria.
 */
const POSITIVE_LEAD_PATTERNS: RegExp[] = [
  // Português
  /\bquero\s+(?:muito\s+|so\s+|apenas\s+)?([a-z\s]{0,48})/g,
  /\bqueremos\s+([a-z\s]{0,48})/g,
  /\bgostava\s+de\s+(?:ver\s+|visitar\s+|ir\s+a\s+)?([a-z\s]{0,48})/g,
  /\bgostaria\s+de\s+(?:ver\s+|visitar\s+)?([a-z\s]{0,48})/g,
  /\bcom\s+muit[ao]s?\s+([a-z\s]{0,48})/g,
  /\bincluir?\s+([a-z\s]{0,48})/g,
  /\bfoco\s+(?:em|nas?|nos?)\s+([a-z\s]{0,48})/g,
  // English
  /\b(?:i|we)\s+want\s+(?:to\s+see\s+|to\s+visit\s+|only\s+|just\s+)?([a-z\s]{0,48})/g,
  /\b(?:i'?d|we'?d)\s+like\s+(?:to\s+see\s+|to\s+visit\s+)?([a-z\s]{0,48})/g,
  /\bfocus\s+on\s+([a-z\s]{0,48})/g,
  /\binclude\s+([a-z\s]{0,48})/g,
  // Español
  /\b(?:quiero|queremos)\s+(?:ver\s+|visitar\s+|solo\s+)?([a-z\s]{0,48})/g,
  /\bincluir\s+([a-z\s]{0,48})/g,
  // Français
  /\b(?:je\s+veux|nous\s+voulons|j'aimerais)\s+(?:voir\s+|visiter\s+)?([a-z\s]{0,48})/g,
  /\binclure\s+([a-z\s]{0,48})/g,
  // Italiano
  /\b(?:voglio|vogliamo|vorrei)\s+(?:vedere\s+|visitare\s+)?([a-z\s]{0,48})/g,
  // Deutsch
  /\b(?:ich\s+moechte|wir\s+moechten)\s+([a-z\s]{0,48})\s*(?:sehen|besuchen)?/g,
];

function matchCategories(window: string): PreferenceCategory[] {
  const found: PreferenceCategory[] = [];
  for (const kind of Object.keys(CATEGORY_TERMS) as PreferenceCategory[]) {
    const re = new RegExp(`\\b(${CATEGORY_TERMS[kind]})\\b`);
    if (re.test(window)) found.push(kind);
  }
  return found;
}

/** Categorias explicitamente pedidas no texto (pedido positivo vinculativo). */
export function parsePositivePreferences(text: string): {
  requiredCategories: PreferenceCategory[];
  matchedPhrases: string[];
} {
  const folded = fold(text);
  const required = new Set<PreferenceCategory>();
  const phrases: string[] = [];
  for (const lead of POSITIVE_LEAD_PATTERNS) {
    lead.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = lead.exec(folded))) {
      const window = m[1] ?? "";
      if (!window.trim()) continue;
      // Uma janela que contém negação não é um pedido positivo.
      if (/\b(?:nao|no|not|sem|sin|sans|senza|keine|ohne)\b/.test(window)) continue;
      const hits = matchCategories(window);
      if (hits.length === 0) continue;
      for (const hit of hits) required.add(hit);
      phrases.push(m[0].trim());
    }
  }
  return { requiredCategories: [...required], matchedPhrases: phrases };
}

export type BuildTripPreferencesInput = {
  /** Texto do pedido ATUAL (nunca o anterior). */
  text: string;
  /** Interesses marcados no formulário desta pesquisa. */
  formInterests?: string[];
  /** Escolha do rádio de refeições nesta pesquisa. */
  mealChoice?: MealPreference | null;
  /**
   * A pessoa tocou de facto no rádio de refeições nesta sessão? O estado
   * inicial do formulário NUNCA conta como escolha explícita (garantia A).
   */
  mealChoiceExplicit?: boolean;
  /** Minutos de refeição configurados (0 equivale a "none"). */
  eatMinutes?: number | null;
  /** Corredor curado reconhecido para este pedido, se existir. */
  corridorId?: string | null;
  maxDeviationKm?: number | null;
};

const FORM_INTEREST_TO_CATEGORY: Record<string, PreferenceCategory> = {
  food: "restaurant",
  gastronomy: "restaurant",
  gastronomia: "restaurant",
  cultural: "museum",
  museums: "museum",
  nature: "nature",
  natureza: "nature",
  beach: "beach",
  praia: "beach",
  shopping: "shopping",
  nightlife: "nightlife",
  viewpoint: "viewpoint",
  markets: "market",
};

/**
 * Constrói o contrato do pedido a partir do estado ATUAL. Determinístico e
 * sem herança: chamar duas vezes com o mesmo input dá o mesmo resultado, e
 * um texto novo apaga completamente o contrato anterior.
 */
export function buildTripPreferences(input: BuildTripPreferencesInput): TripPreferences {
  const text = input.text ?? "";
  const negatives: NegativePreferences = parseNegativePreferences(text);
  const positives = parsePositivePreferences(text);
  const sources: Record<string, PreferenceSource> = {};

  const excluded = new Set<PreferenceCategory>(negatives.excludedCategories);
  for (const category of excluded) sources[`excluded:${category}`] = "text";

  const required = new Set<PreferenceCategory>();
  for (const category of positives.requiredCategories) {
    if (excluded.has(category)) continue; // exclusão explícita ganha sempre
    required.add(category);
    sources[`required:${category}`] = "text";
  }
  for (const interest of input.formInterests ?? []) {
    const category = FORM_INTEREST_TO_CATEGORY[fold(interest)];
    if (!category || excluded.has(category) || required.has(category)) continue;
    required.add(category);
    sources[`required:${category}`] = "form";
  }

  const mealPreference = resolveMealPreference({
    choice: input.mealChoice ?? undefined,
    // Estado inicial do rádio ≠ escolha explícita: só uma interação real
    // da pessoa nesta sessão pode ativar "restaurant" por padrão.
    choiceExplicit: input.mealChoiceExplicit === true,
    eatMinutes: input.eatMinutes ?? undefined,
    requestText: text,
  });
  sources["mealPreference"] = input.mealChoiceExplicit
    ? "form"
    : (input.eatMinutes ?? null) === 0 || /\b(sem|no|sin|sans|senza|keine)\b/.test(fold(text))
      ? "text"
      : "default";

  // A escolha EXPLÍCITA "restaurante" limpa qualquer negação arrastada.
  // O estado inicial do rádio (não tocado) nunca conta como escolha.
  if (input.mealChoice === "restaurant" && input.mealChoiceExplicit === true) {
    excluded.delete("restaurant");
    required.add("restaurant");
    sources["required:restaurant"] = "form";
  }

  const specialties = extractLocalSpecialties(text);
  // Nomes próprios não reconhecidos entram como itens nomeados dinâmicos: são
  // validados contra fontes reais, nunca assumidos como categoria.
  const named = extractNamedItems(text);
  const requiredNamedItems = [
    ...new Set([
      ...specialties.map((specialty) => specialty.query),
      ...named.filter((item) => item.polarity === "required").map((item) => item.name),
    ]),
  ];
  const excludedNamedItems = [
    ...new Set(named.filter((item) => item.polarity === "excluded").map((item) => item.name)),
  ];
  for (const item of named) sources[`${item.polarity}Item:${item.key}`] = "text";

  return {
    requiredCategories: [...required],
    excludedCategories: [...excluded],
    requiredNamedItems,
    excludedNamedItems,
    foodSpecialties: specialties,
    routeConstraints: {
      corridorId: input.corridorId ?? undefined,
      followOfficialCorridor: !!input.corridorId,
      maxDeviationKm: input.maxDeviationKm ?? undefined,
    },
    mealPreference,
    sources,
    matchedPhrases: [...negatives.matchedPhrases, ...positives.matchedPhrases],
  };
}

/** Vista compatível com o filtro de exclusões já usado nos motores. */
export function toNegativePreferences(prefs: TripPreferences): NegativePreferences {
  return {
    excludedCategories: prefs.excludedCategories,
    matchedPhrases: prefs.matchedPhrases,
  };
}

const CATEGORY_TO_INTEREST: Partial<Record<PreferenceCategory, string>> = {
  restaurant: "food",
  cafe: "food",
  bakery: "food",
  museum: "cultural",
  monument: "cultural",
  church: "cultural",
  park: "nature",
  nature: "nature",
  beach: "nature",
  viewpoint: "viewpoint",
  market: "market",
  shopping: "shopping",
  nightlife: "nightlife",
  castle: "cultural",
  winery: "food",
  thermal: "nature",
  zoo: "family",
  library: "cultural",
  theme_park: "family",
};

/** Interesses de pesquisa derivados do contrato (exclusões já aplicadas). */
export function interestsFromPreferences(prefs: TripPreferences, base: string[] = []): string[] {
  const excludedInterests = new Set(
    prefs.excludedCategories.map((category) => CATEGORY_TO_INTEREST[category]).filter(Boolean),
  );
  const out = new Set<string>();
  for (const interest of base) if (!excludedInterests.has(interest)) out.add(interest);
  for (const category of prefs.requiredCategories) {
    const interest = CATEGORY_TO_INTEREST[category];
    if (interest && !excludedInterests.has(interest)) out.add(interest);
  }
  return [...out];
}
